import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import DatePicker from 'react-datepicker'
import { ClipboardText, CaretDownIcon } from '@phosphor-icons/react'
import { User } from '../../types/User'
import { useLazyGetAllUsersTimesOffReportQuery } from '../../redux/timeoff.endpoints'
import { UsersTimesOff } from '../../types/TimeOff'
import { useGetAllTribesQuery } from '../../redux/tribe.endpoints'
import { useGetAllTeamsQuery } from '../../redux/team.endpoints'
import ReportTable from '../../components/ReportTable/ReportTable'
import { SelectOption } from '../../types/SelectOption'
import { useLazyGetAllRolesQuery } from '../../redux/role.endpoints'
import { Role } from '../../types/Roles'
import moment from 'moment'
import { clipTimeoffToReportRange, getBusinessDaysBetween } from '../../hooks/useTimeoffCalculator'
import { classifyTimeOffBucket, TIMEFRAME_LABELS } from '../../utils/timeOffBuckets'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../redux/store'
import { setEndDate, setStartDate } from '../../redux/date.slice'
import { DateFieldInput } from '../../components/EmployeeForm/DateFieldInput'
import { FormField } from '../../components/EmployeeForm/FormTextField'
import { FormCheckbox } from '../../components/EmployeeForm/FormCheckbox'
import { TimeOffEntryDetail } from '../../types/TimeOff'

interface Props {
  profile: User
  companyId: number
}

const ALL_TRIBES = '0'
const TABLE_HEADERS = ['Guild', 'Tribe', 'Team', 'Days Taken', 'Days Planned', 'Time Off']

const TimeOffReport = ({ profile, companyId }: Props) => {
  const [usersTimesOff, setUsersTimesOff] = useState<UsersTimesOff[]>([])

  const [availableTribes, setAvailableTribes] = useState([] as SelectOption[])
  const [availableTeams, setAvailableTeams] = useState([] as SelectOption[])
  const [availableGuilds, setAvailableGuilds] = useState([] as SelectOption[])
  const [selectedGuild, setSelectedGuild] = useState('')
  const [selectedTribe, setSelectedTribe] = useState(ALL_TRIBES)
  const [selectedTeam, setSelectedTeam] = useState('')
  const [loadingTableInfo, setLoadingTableInfo] = useState<boolean>(false)
  const [reportGenerated, setReportGenerated] = useState<boolean>(false)
  const [activeMembersOnly, setActiveMembersOnly] = useState<boolean>(true)
  const [membersNoTimeOff, setMemberNoTimeOff] = useState<boolean>(false)

  const [getAllRoles, getAllRolesResponse] = useLazyGetAllRolesQuery()
  const [guilds, setGuilds] = useState<Array<Role>>([])
  const dispatch = useDispatch<AppDispatch>()
  const startDate = useSelector((state: RootState) => state.date.startDate)
  const endDate = useSelector((state: RootState) => state.date.endDate)

  useEffect(() => {
    getAllRoles()
  }, [])

  const { data: teams } = useGetAllTeamsQuery(companyId)

  const [getUsersTimesOff, getUsersTimesOffResponse] =
    useLazyGetAllUsersTimesOffReportQuery()
  const { data: tribes } = useGetAllTribesQuery({
    companyId,
    pageSize: 0,
    pageNumber: 0,
    sortBy: 'id',
    searchQuery: '',
    sortType: 'DESC',
  })

  const createReport = async () => {
    setReportGenerated(true)
    setLoadingTableInfo(true)
    await getUsersTimesOff({
      userId: profile.id.toString(),
      teamId: selectedTeam,
      clientId: '',
      tribeId: selectedTribe,
      guild: selectedGuild,
      from: startDate ? startDate.getTime().toString() : '',
      to: endDate ? endDate.getTime().toString() : '',
      activeMembersOnly: activeMembersOnly.toString(),
      membersNoTimeOff: membersNoTimeOff.toString(),
    }).then(() => {
      setLoadingTableInfo(false)
    })
  }

  const calculateNextTimeOff = (
    timeoffs: any[],
    reportRange?: { rangeStart: Date; rangeEnd: Date },
  ): TimeOffEntryDetail[] => {
    if (timeoffs && timeoffs.length > 0) {
      const result = timeoffs
        .map(time => {
          const clipped = reportRange
            ? clipTimeoffToReportRange(
                time,
                reportRange.rangeStart,
                reportRange.rangeEnd,
              )
            : null

          if (!clipped && reportRange) {
            return null
          }

          const startDate = (clipped?.startDate ?? moment(time.startDate)).clone()
          const endDate = (clipped?.endDate ?? moment(time.endDate)).clone()

          const bucket = classifyTimeOffBucket(endDate, time.startDate)

          let text = `${startDate.format('MMM DD, YYYY')} - ${endDate.format('MMM DD, YYYY')}`

          if (startDate.isSame(endDate, 'day')) text = `${startDate.format('MMM DD, YYYY')}`

          return {
            text,
            colorClass: `rp-pill rp-pill--${bucket}`,
            startDate,
            endDate,
            days: getBusinessDaysBetween(startDate, endDate),
            status: bucket === 'past' ? 'Taken' : 'Planned',
            timeframe: TIMEFRAME_LABELS[bucket],
          } as TimeOffEntryDetail
        })
        .filter((item): item is TimeOffEntryDetail => item != null);

      return result
    }

    return []
  }

  const dateValidation = () => {
    return startDate === undefined || endDate === undefined;
  }

  const cleanTableInfo = () => {
    setUsersTimesOff([])
  }

  const filterTeamByTribe = (): SelectOption[] => {
    const tribeTeamOptions: SelectOption[] = []
    if (selectedTribe !== ALL_TRIBES && tribes) {
      const selected = tribes.find(item => item.id === parseInt(selectedTribe))
      selected &&
        selected.teams.forEach(team => {
          tribeTeamOptions.push({ label: team.name, value: `${team.id}` })
        })
    }
    return tribeTeamOptions.length > 0
      ? [
        { label: 'All Teams', value: '' },
        { label: 'Staff Augmentation Loan', value: '-1' },
        ...tribeTeamOptions,
      ]
      : []
  }

  useEffect(() => {
    if (getAllRolesResponse && getAllRolesResponse.data) {
      const allGuildOption = { label: 'All Guilds', value: '' }
      const guildRoles = getAllRolesResponse.data.map((guild) => ({
        label: guild.role,
        value: `${guild.id}`
      }))
      setGuilds(getAllRolesResponse.data)
      setAvailableGuilds([allGuildOption, ...guildRoles])
    }
  }, [getAllRolesResponse])

  useEffect(() => {
    if (!tribes) {
      setAvailableTribes([])
      return
    }
    setAvailableTribes([
      { label: 'All', value: ALL_TRIBES },
      ...tribes.map(tribe => ({ label: tribe.name, value: `${tribe.id}` })),
    ])
  }, [tribes])

  useEffect(() => {
    if (selectedTribe !== ALL_TRIBES) {
      setAvailableTeams(filterTeamByTribe())
      return
    }
    if (teams) {
      const teamOptions: SelectOption[] = [
        { label: 'All Teams', value: '' },
        { label: 'Staff Augmentation Loan', value: '-1' },
      ]
      teams.forEach(team => {
        teamOptions.push({ label: team.name, value: `${team.id}` })
      })
      setAvailableTeams(teamOptions)
      return
    }
    setAvailableTeams([])
  }, [selectedTribe, tribes, teams])

  useEffect(() => {
    if (getUsersTimesOffResponse && getUsersTimesOffResponse.data) {
      setUsersTimesOff(getUsersTimesOffResponse.data)
    }
  }, [getUsersTimesOffResponse])

  useEffect(() => {
    cleanTableInfo()
  }, [selectedTribe])

  useEffect(() => {
    cleanTableInfo()
  }, [selectedTeam])

  useEffect(() => {
    setReportGenerated(false)
  }, [selectedTribe, selectedTeam, startDate, endDate])

  return (
    <>
      <Helmet title="Time Off - Reports" />
      <div className="page-content reports-page">
        <h1 className="reports-page__title">Time Off Report</h1>

        <div className="reports-card">
          <div className="reports-card__header">
            <h3 className="reports-card__title">Create Report</h3>
            <button
              type="button"
              className="reports-btn-create"
              onClick={() => createReport()}
              disabled={dateValidation() || loadingTableInfo}
            >
              <ClipboardText size={16} weight="regular" />
              Create Report
            </button>
          </div>

          <div className="reports-filter-grid">
            <FormField label="From" required>
              <DatePicker
                selected={startDate}
                maxDate={endDate}
                placeholderText="Select a date"
                customInput={
                  <DateFieldInput
                    clearable
                    onClear={() => dispatch(setStartDate(undefined))}
                  />
                }
                onChange={date => {
                  if (date) dispatch(setStartDate(date))
                }}
              />
            </FormField>
            <FormField label="To" required>
              <DatePicker
                selected={endDate}
                minDate={startDate}
                placeholderText="Select a date"
                customInput={
                  <DateFieldInput
                    clearable
                    onClear={() => dispatch(setEndDate(undefined))}
                  />
                }
                onChange={date => {
                  if (date) dispatch(setEndDate(date))
                }}
              />
            </FormField>
            <FormField label="Guilds" htmlFor="reportsGuild">
              <div className="employee-form__select-wrapper">
                <select
                  id="reportsGuild"
                  className="employee-form__select-input"
                  value={selectedGuild}
                  onChange={e => setSelectedGuild(e.target.value)}
                >
                  {availableGuilds.map(guild => (
                    <option key={guild.value} value={guild.value}>
                      {guild.label}
                    </option>
                  ))}
                </select>
                <span className="employee-form__select-icon">
                  <CaretDownIcon size={14} weight="regular" />
                </span>
              </div>
            </FormField>
            <FormField label="Tribe" htmlFor="reportsTribe">
              <div className="employee-form__select-wrapper">
                <select
                  id="reportsTribe"
                  className="employee-form__select-input"
                  value={selectedTribe}
                  onChange={e => {
                    setSelectedTribe(e.target.value)
                    setSelectedTeam('')
                  }}
                >
                  {availableTribes.map(tribe => (
                    <option key={tribe.value} value={tribe.value}>
                      {tribe.label}
                    </option>
                  ))}
                </select>
                <span className="employee-form__select-icon">
                  <CaretDownIcon size={14} weight="regular" />
                </span>
              </div>
            </FormField>
            <FormField label="Team" htmlFor="reportsTeam">
              <div className="employee-form__select-wrapper">
                <select
                  id="reportsTeam"
                  className="employee-form__select-input"
                  value={selectedTeam}
                  onChange={e => setSelectedTeam(e.target.value)}
                >
                  {availableTeams.map(team => (
                    <option key={team.value} value={team.value}>
                      {team.label}
                    </option>
                  ))}
                </select>
                <span className="employee-form__select-icon">
                  <CaretDownIcon size={14} weight="regular" />
                </span>
              </div>
            </FormField>
          </div>

          <div className="reports-checks">
            <FormCheckbox
              id="activeMembersOnly"
              checked={activeMembersOnly}
              onChange={setActiveMembersOnly}
              label="Active Members Only"
            />
            <FormCheckbox
              id="membersNoTimeOff"
              checked={membersNoTimeOff}
              onChange={setMemberNoTimeOff}
              label="Include Team Members with No Time Off"
            />
          </div>
        </div>

        {reportGenerated && (
          <ReportTable
            usersInfo={usersTimesOff}
            calculateNextTimeOff={calculateNextTimeOff}
            selectedReport="Time Off"
            selectedTeam={selectedTeam}
            tableHeaders={TABLE_HEADERS}
            guilds={guilds}
            loading={loadingTableInfo}
          />
        )}
      </div>
    </>
  )
}

export default TimeOffReport
